import { CardShell } from '@atlas/ui-components'
import { Button, Combobox, Input, MultiCombobox, Switch, Textarea } from '@atlas/ui-primitives'
import { useState } from 'react'
import type { PromptListRecord, PromptVariable, PromptVariableListItem } from '@/domain/prompt.types'
import { fillableVariables, PromptVariableType, withoutBlankValues } from '@/domain/prompt.types'

export interface PromptPreviewFormProps {
	variables: PromptVariable[]
	/** Items for `list`-typed variables, keyed by list code. */
	lists: PromptListRecord[]
	onSubmit: (values: Record<string, unknown>) => void
	isSubmitting?: boolean
	/** Server-side render output, or the error it refused with. */
	rendered?: string
	error?: string
	warnings?: string[]
	/**
	 * Keys the body no longer references. Their fields are still rendered — the value
	 * may be wanted again — but marked, because the editor beside this form already
	 * calls them unused and a form that silently discards what you type contradicts it.
	 */
	unusedKeys?: string[]
}

/**
 * A form generated from the variable schema, one control per declared type.
 *
 * The resolved text always comes from the SERVER render rather than substituting in
 * the browser: a preview that used its own renderer could diverge from what actually
 * executes, which is the one thing a preview must not do.
 */
export function PromptPreviewForm({
	variables,
	lists,
	onSubmit,
	isSubmitting = false,
	rendered,
	error,
	warnings = [],
	unusedKeys = [],
}: PromptPreviewFormProps) {
	const [values, setValues] = useState<Record<string, unknown>>({})
	const set = (key: string, value: unknown) => setValues((v) => ({ ...v, [key]: value }))

	// A `join` is computed from the variables it names, so a field for it would ask
	// for a value the render overwrites.
	const fields = fillableVariables(variables)

	return (
		<CardShell as="section" aria-label="Preview" className="p-4">
			<h3 className="mb-3 text-ds-sm font-medium text-ds-text">Preview</h3>

			<form
				onSubmit={(e) => {
					e.preventDefault()
					// Empty optionals are omitted rather than sent as '': the render engine
					// treats an absent value as "use the default", which is what a blank
					// field means here. Shared with the required-field checks, so the two
					// can never disagree about what "empty" is.
					onSubmit(withoutBlankValues(values))
				}}
			>
				{fields.length === 0 ? (
					<p className="text-ds-sm text-ds-text-muted">This prompt takes no variables.</p>
				) : (
					<div className="space-y-3">
						{fields.map((variable) => (
							// A variable the body no longer references is dimmed and labelled, not
							// hidden: the panel beside this one already says "Declared but unused",
							// and a form that asks for a value it then discards contradicts it.
							<div key={variable.key} className={unusedKeys.includes(variable.key) ? 'opacity-60' : undefined}>
								<VariableControl
									variable={variable}
									value={values[variable.key]}
									items={lists.find((l) => l.code === variable.listCode)?.items ?? []}
									onChange={(v) => set(variable.key, v)}
								/>
								{unusedKeys.includes(variable.key) ? (
									<p className="mt-1 text-ds-2xs text-ds-text-subtle">
										Not referenced by the body — this value will not be rendered.
									</p>
								) : null}
							</div>
						))}
					</div>
				)}

				<Button type="submit" className="mt-4" isDisabled={isSubmitting}>
					{isSubmitting ? 'Rendering…' : 'Render'}
				</Button>
			</form>

			<PreviewOutcome rendered={rendered} error={error} warnings={warnings} />
		</CardShell>
	)
}

/**
 * One input, chosen by the variable's declared type.
 *
 * Exported because a test case supplies the same payload a preview does, so the
 * add-case form is this control per variable — duplicating the type-to-control
 * mapping would mean two places to update when a variable type is added.
 */
export function VariableControl({
	variable,
	value,
	items,
	onChange,
}: {
	variable: PromptVariable
	value: unknown
	items: PromptVariableListItem[]
	onChange: (value: unknown) => void
}) {
	const label = `${variable.label || variable.key}${variable.required ? ' *' : ''}`

	if (variable.type === PromptVariableType.BOOLEAN) {
		return (
			<Switch isSelected={value === true} onChange={onChange}>
				{label}
			</Switch>
		)
	}

	if (variable.type === PromptVariableType.EACH) {
		return <EachControl variable={variable} value={value} onChange={onChange} />
	}

	const isChoice = variable.type === PromptVariableType.ENUM || variable.type === PromptVariableType.LIST

	// `enum` owns its options; `list` draws them from the shared list's items.
	const choices =
		variable.type === PromptVariableType.ENUM
			? (variable.options ?? []).map((o) => ({ value: o, label: o }))
			: items.map((i) => ({ value: i.value, label: i.label }))

	return (
		<label className="block">
			<span className="mb-1 block text-ds-2xs text-ds-text-subtle">{label}</span>

			{variable.type === PromptVariableType.TEXT ? (
				<Textarea aria-label={label} rows={3} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
			) : isChoice ? (
				// Searchable in both modes, because a list can be long: `species` alone
				// offers 90 options, and scanning an unsearchable dropdown for one of them
				// was the slowest part of filling this form.
				variable.multiple === true ? (
					<MultiCombobox
						aria-label={label}
						options={choices}
						value={(value as string[]) ?? []}
						onChange={(next) => onChange(next)}
					/>
				) : (
					<Combobox
						aria-label={label}
						options={choices}
						value={value === undefined || value === '' ? null : String(value)}
						placeholder="Select…"
						onChange={(next) => onChange(next ?? '')}
					/>
				)
			) : (
				<Input
					aria-label={label}
					type={
						variable.type === PromptVariableType.NUMBER
							? 'number'
							: variable.type === PromptVariableType.DATE
								? 'date'
								: 'text'
					}
					value={String(value ?? '')}
					onChange={(e) =>
						onChange(
							variable.type === PromptVariableType.NUMBER && e.target.value !== ''
								? Number(e.target.value)
								: e.target.value,
						)
					}
				/>
			)}
		</label>
	)
}

/** What came back: an error, any warnings, and the resolved text. */
function PreviewOutcome({ rendered, error, warnings }: { rendered?: string; error?: string; warnings: string[] }) {
	return (
		<>
			{error ? (
				<p role="alert" className="mt-4 text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}

			{warnings.length > 0 ? (
				<ul className="mt-3 space-y-1">
					{warnings.map((w) => (
						<li key={w} className="text-ds-sm text-ds-warning">
							{w}
						</li>
					))}
				</ul>
			) : null}

			{rendered !== undefined ? (
				<div className="mt-4">
					<h4 className="mb-1 text-ds-2xs uppercase tracking-wide text-ds-text-subtle">Resolved text</h4>
					<pre className="whitespace-pre-wrap rounded-ds-md border border-ds-border bg-ds-surface p-3 text-ds-sm text-ds-text">
						{rendered}
					</pre>
				</div>
			) : null}
		</>
	)
}

/**
 * An `each` payload as JSON.
 *
 * An arbitrary array of objects has no sensible typed widget — the item shape is
 * whatever the template references — so the honest control is the raw structure with
 * a parse error surfaced inline rather than swallowed.
 */
function EachControl({
	variable,
	value,
	onChange,
}: {
	variable: PromptVariable
	value: unknown
	onChange: (value: unknown) => void
}) {
	const label = `${variable.label || variable.key}${variable.required ? ' *' : ''}`
	const [text, setText] = useState(value === undefined ? '' : JSON.stringify(value, null, 2))
	const [parseError, setParseError] = useState<string | null>(null)

	return (
		<label className="block">
			<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
				{label} — JSON array of objects{variable.itemTemplate ? ` for ${variable.itemTemplate}` : ''}
			</span>
			<Textarea
				aria-label={label}
				rows={4}
				value={text}
				className="font-mono"
				placeholder='[{ "severity": "blocking", "statement": "…" }]'
				onChange={(e) => {
					setText(e.target.value)
					if (e.target.value.trim() === '') {
						setParseError(null)
						onChange(undefined)
						return
					}
					try {
						onChange(JSON.parse(e.target.value))
						setParseError(null)
					} catch {
						// Held locally rather than sent: an unparseable draft is the user
						// mid-typing, not a value to render.
						setParseError('Not valid JSON yet')
					}
				}}
			/>
			{parseError ? <span className="mt-1 block text-ds-2xs text-ds-warning">{parseError}</span> : null}
		</label>
	)
}
